import React from 'react';
import { Modal, Loading, Icon } from '@brighterion/br-ui';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { NumberService } from 'Services';
import { useTranslation } from 'react-i18next';
import { getTranslationValidationHandling } from 'Hooks/query-hooks/helper';
import { useAccessTokenStore } from 'Hooks/zustand/useAccessTokenStore';
import useUserConfig from 'Hooks/query-hooks/useUserConfig';
import RouteLoading from 'Components/RouteLoading';
import errorController from 'Components/ErrorScreens';
import FieldId from '../FieldId';
import FieldThreshold from '../FieldThreshold';
import FieldMerchantName from '../FieldMerchantName';
import FieldDescription from '../FieldDescription';
import { ThresholdSchema, IdSchema } from '../schema';
import { MerchantModalProps } from '../types';
import styles from './AddModal.css';

const AddSchema = (
    showMerchantThreshold: boolean,
    validationT: (s: string, options?: any) => string,
    maxGlobalAggregateThreshold: number
) =>
    Yup.object().shape({
        id: IdSchema(validationT),
        overallThreshold: ThresholdSchema(showMerchantThreshold, validationT, maxGlobalAggregateThreshold)
    });

const AddMerchantModal = ({
    isOpen,
    closeModal,
    handleSubmit,
    globalThreshold,
    isLoading,
    error
}: MerchantModalProps<MerchantFormState>) => {
    const { t } = useTranslation();
    const validationT = getTranslationValidationHandling(t);
    const [showMerchantThreshold, setShowMerchantThreshold] = React.useState(false);
    const globalThresholdText = t('accept-if-score-lteq-threshold', {
        overallThreshold: NumberService.getLocaleNumber(globalThreshold)
    });
    const merchantThresholdText = (
        <div className={styles.merchantThresholdContainer}>
            <Icon name='accept' size='sm' />
            <div className={styles.merchantThresholdActionText}>Accept</div>
            <div className={styles.merchantThresholdStatement}>{'If score <='}</div>
        </div>
    );

    const { getUserInfo } = useAccessTokenStore.getState();
    const uniqueUserTokenId = getUserInfo().tenantKey;
    const userConfig = useUserConfig(uniqueUserTokenId);

    if (userConfig.isLoading) {
        return <RouteLoading />;
    }

    if (userConfig.isError) {
        const { errorCmp } = errorController([userConfig.error]);
        return errorCmp;
    }

    return (
        <Modal
            isOpen={isOpen}
            closeModal={closeModal}
            containerClass={styles.modalContainer}
            closeOnClickOutside={!isLoading}
        >
            <Modal.Title
                id='add-merchant-modal-title'
                title={t('Add Merchant')}
                onClose={closeModal}
                isSubmitting={isLoading}
            />
            <Formik
                initialValues={{ id: '', merchantName: '', merchantDescription: '', overallThreshold: undefined }}
                validationSchema={AddSchema(
                    showMerchantThreshold,
                    validationT,
                    userConfig.data.constants.maxGlobalAggregateThreshold
                )}
                onSubmit={handleSubmit}
            >
                {({ dirty, isValid, submitForm, errors, handleChange, handleBlur, setFieldValue, setFieldTouched }) => {
                    const disableSave = !dirty || !isValid || isLoading;

                    return (
                        <div className={styles.formContainer}>
                            <FieldId
                                className={styles.formInput}
                                name='id'
                                label={t('Merchant ID')}
                                handleBlur={handleBlur}
                                handleChange={(event: React.ChangeEvent) => {
                                    handleChange(event);
                                    setFieldTouched('id', true, false);
                                }}
                            />
                            <FieldMerchantName handleBlur={handleBlur} handleChange={handleChange} />
                            <FieldDescription handleBlur={handleBlur} handleChange={handleChange} />
                            <FieldThreshold
                                globalThreshold={globalThreshold}
                                globalThresholdText={globalThresholdText}
                                merchantThresholdText={merchantThresholdText}
                                errors={errors}
                                setFieldValue={setFieldValue}
                                showMerchantThreshold={showMerchantThreshold}
                                setShowMerchantThreshold={setShowMerchantThreshold}
                            />
                            <Modal.Footer
                                id='add-merchant-modal-footer'
                                onCloseText={t('Cancel')}
                                onActionText={isLoading ? <Loading size='sm' fillName='white' /> : t('Save')}
                                onAction={submitForm}
                                onClose={closeModal}
                                disableOnCancel={isLoading}
                                disableOnAction={disableSave}
                            >
                                {isLoading && <span className={styles.saving}>{t('savingDots')}</span>}
                                {error && <span className={styles.errMsg}>{error.data.error.message}</span>}
                            </Modal.Footer>
                        </div>
                    );
                }}
            </Formik>
        </Modal>
    );
};

export default AddMerchantModal;
